import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Send, StopCircle, Paperclip, Sparkles, ChevronDown, Loader2, X, History, Image as ImageIcon, Code, Copy, Mic, MicOff } from 'lucide-react';
import { useChatsStore, useModelsStore, useUserStore, useConfigStore, useSettingsStore } from '../../stores';
import { getChatById, createChat, updateChat, getModels } from '../../services/api';
import { useWebLlm } from '../WebLlmProvider';
import modelManager from '../../services/modelManager';
import mawiDB from '../../services/indexedDBService';
import { speechRecognitionService } from '../../services/speechRecognitionService';
import { Button } from '../../mawi-ui';
import { Input } from '../../mawi-ui';
import { ScrollArea } from '../../mawi-ui';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../mawi-ui';
import { Breadcrumb } from '../../mawi-ui';
import { SkeletonChatMessage } from '../../mawi-ui';
import { LoadingSpinner, LoadingOverlay } from '../../mawi-ui';
import { Virtuoso } from 'react-virtuoso';
import { useKeyboardShortcuts, commonKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { useTouchGestures } from '../../hooks/useTouchGestures';
import { useMemoryManagement } from '../../hooks/useMemoryManagement';
import { SmartSuggestions } from '../../mawi-ui';
import { ContentSummarization } from '../../mawi-ui';
import { useInteractionLogger } from '../../hooks/useInteractionLogger';
import EnhancedMessage from './EnhancedMessage';
import ConversationManager from './ConversationManager';
import AIChatInput from './AIChatInput';
import { ChatParametersPanel, ChatParameters, defaultParameters } from './ChatParameters';
import { cn, generateId, formatDate } from '../../utils';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { usePageHeader } from '../../contexts/PageHeaderContext';

// Advanced thinking indicator with progress and streaming support
interface ThinkingIndicatorProps {
  status?: string;
  progress?: number;
  isStreaming?: boolean;
}

const ThinkingIndicator: React.FC<ThinkingIndicatorProps> = ({ status, progress, isStreaming }) => {
  // Show model loading progress if model is loading
  if (status === 'loading' && progress !== undefined) {
    return (
      <div className="flex flex-col gap-2 py-2">
        <LoadingSpinner size="sm" text="Loading model..." />
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-500 h-2 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="text-xs text-gray-400">{Math.round(progress)}% loaded</span>
      </div>
    );
  }

  // Show streaming indicator for real-time responses
  if (isStreaming) {
    return (
      <div className="flex items-center gap-1 py-2">
        <span className="text-sm text-gray-500 mr-2">Generating</span>
        <div className="flex gap-1">
          <div className="w-1 h-1 bg-blue-500 rounded-full animate-pulse"></div>
          <div className="w-1 h-1 bg-blue-500 rounded-full animate-pulse" style={{ animationDelay: '200ms' }}></div>
          <div className="w-1 h-1 bg-blue-500 rounded-full animate-pulse" style={{ animationDelay: '400ms' }}></div>
        </div>
      </div>
    );
  }

  // Show thinking animation for initial response preparation
  return (
    <div className="flex items-center gap-1 py-2">
      <span className="text-sm text-gray-500 mr-2">Thinking</span>
      <div className="flex gap-1">
        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
      </div>
    </div>
  );
};

function Chat() {
  const { chatId: urlChatId } = useParams();
  const navigate = useNavigate();
  const {
    currentChat,
    currentChatId,
    setCurrentChatId,
    setChatTitle,
    chatTitle,
    chats,
    setChats,
    addChat,
    updateChat: updateChatInStore,
  } = useChatsStore();
  const { models, setModels: setModelsStore } = useModelsStore();

  const { user } = useUserStore();
  const { config } = useConfigStore();
  const { settings } = useSettingsStore();
  const { engine, status, progress, currentModelId, setModelUrl, switchModel, isSupported, isStreaming, setIsStreaming, backend, cachedModels } = useWebLlm();
  const { logInteraction } = useInteractionLogger();

  const [messages, setMessages] = useState<any[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState('');
  const [showModelSelector, setShowModelSelector] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [micPermissionStatus, setMicPermissionStatus] = useState<'checking' | 'granted' | 'blocked' | 'prompt'>('checking');
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const [chatParameters, setChatParameters] = useState<ChatParameters>(defaultParameters);
  const [modelSwitchInProgress, setModelSwitchInProgress] = useState(false);
  const [showParameters, setShowParameters] = useState(false);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showChatHistory, setShowChatHistory] = useState(false);

  const [messagesLoading, setMessagesLoading] = useState(false);
  const [showCodeSnippet, setShowCodeSnippet] = useState(false);
  const [chatHistoryList, setChatHistoryList] = useState<any[]>([]);

  // Memory management for chat component
  const memoryManager = useMemoryManagement({
    maxCacheSize: 100,
    cleanupInterval: 300000, // 5 minutes
  });

  // Debug logging
  console.log('Chat component render - models:', models?.length || 0, 'showModelSelector:', showModelSelector, 'selectedModel:', selectedModel);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    // Use setTimeout to allow DOM to update before scrolling
    setTimeout(() => {
      scrollToBottom();
    }, 50);
  }, [messages]);

  // Load models
  useEffect(() => {
    const loadModels = async () => {
      try {
        console.log('Loading models...');
        const allModels = await modelManager.getAvailableModels();
        console.log('Available models:', allModels);

        if (allModels.length === 0) {
          console.log('No models available, redirecting to models page');
          navigate('/models');
          return;
        }

        const transformedModels = allModels.map(model => ({
          id: model.id,
          name: model.name,
          owned_by: 'arena' as const,
          size: model.size,
        }));

        console.log('Setting models store:', transformedModels);
        setModelsStore(transformedModels);

        if (transformedModels.length > 0 && !selectedModel && !modelSwitchInProgress) {
          const defaultModelId = transformedModels[0].id;
          console.log('Loading default model:', defaultModelId);
          setSelectedModel(defaultModelId);
          setModelSwitchInProgress(true);
          // Use switchModel for proper model loading
          switchModel(defaultModelId).catch(error => {
            console.error('Failed to load default model:', error);
          }).finally(() => {
            setModelSwitchInProgress(false);
          });
        }
      } catch (error) {
        console.error('Failed to load models:', error);
      }
    };
    loadModels();
  }, [setModelsStore, navigate]);

  // Load last selected model on initial page load
  useEffect(() => {
    const loadLastSelectedModel = async () => {
      try {
        const selectedPrefs = await mawiDB.llm.getSelectedModelPreferences();
        if (selectedPrefs) {
          console.log('Found last selected model:', selectedPrefs.modelId);
          setSelectedModel(selectedPrefs.modelId);
          setModelSwitchInProgress(true);
          switchModel(selectedPrefs.modelId).catch(() => {
            // Fall back to default if model fails to load
            setModelSwitchInProgress(false);
          });
        }
      } catch (err) {
        console.log('No previously selected model, will load default');
      }
    };
    
    loadLastSelectedModel();
  }, []);

  // Load chat history list when panel is opened
  useEffect(() => {
    if (showChatHistory) {
      const loadChatHistory = async () => {
        try {
          const history = await mawiDB.store.getChatHistory(user?.id || 'anonymous');
          setChatHistoryList(history.sort((a, b) => 
            new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime()
          ));
        } catch (err) {
          console.warn('Failed to load chat history:', err);
          setChatHistoryList([]);
        }
      };
      loadChatHistory();
    }
  }, [showChatHistory, user?.id]);

  // Load chat history on component mount
  useEffect(() => {
    const loadChatHistory = async () => {
      try {
        const history = await mawiDB.store.getChatHistory(user?.id || 'anonymous');
        setChatHistoryList(history.sort((a, b) => 
          new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime()
        ));
      } catch (err) {
        console.warn('Failed to load chat history:', err);
        setChatHistoryList([]);
      }
    };
    loadChatHistory();
  }, [user?.id]);

  // Update model URL when selectedModel changes
  useEffect(() => {
    if (selectedModel) {
      const modelUrl = modelManager.getModelUrl(selectedModel);
      if (modelUrl) {
        console.log('Setting model URL for', selectedModel, ':', modelUrl);
        setModelUrl(modelUrl);
      } else {
        console.warn('No model URL found for', selectedModel);
      }

      // Load model preferences from database or create defaults
      mawiDB.llm.getOrCreateModelPreferences(selectedModel, {
        temperature: defaultParameters.temperature,
        maxTokens: defaultParameters.maxTokens,
        topP: defaultParameters.topP,
        presencePenalty: defaultParameters.presencePenalty,
        frequencyPenalty: defaultParameters.frequencyPenalty,
        systemMessage: defaultParameters.systemMessage
      }).then(prefs => {
        console.log(`Loaded preferences for model ${selectedModel}`, prefs);
        setChatParameters({
          temperature: prefs.temperature,
          maxTokens: prefs.maxTokens,
          topP: prefs.topP,
          presencePenalty: prefs.presencePenalty,
          frequencyPenalty: prefs.frequencyPenalty,
          systemMessage: prefs.systemMessage
        });
      }).catch(err => {
        console.warn('Failed to load preferences, using defaults', err);
      });
    }
  }, [selectedModel, setModelUrl]);

  // Auto-save chat parameters to database whenever they change
  useEffect(() => {
    if (selectedModel) {
      mawiDB.llm.updateModelPreferences(selectedModel, {
        ...chatParameters,
        isSelected: true
      }).catch(err => {
        console.debug('Preferences auto-save skipped (entry may not exist yet)', err);
      });
    }
  }, [chatParameters, selectedModel]);

  // Load chat if URL has chatId
  useEffect(() => {
    const loadChat = async () => {
      if (urlChatId) {
        try {
          const chat = await getChatById(urlChatId);
          setCurrentChatId(chat.id);
          setChatTitle(chat.title);
          setMessages(chat.messages || []);
        } catch (error) {
          console.error('Failed to load chat:', error);
          // Reset to new chat state on error
          setCurrentChatId(null);
          setChatTitle('New Chat');
          setMessages([]);
        }
      }
    };
    loadChat();
  }, [urlChatId, setCurrentChatId, setChatTitle]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    // Log interaction start
    await logInteraction({
      toolUsed: 'chat',
      query: input,
      success: true,
      metadata: {
        model: selectedModel,
        hasAttachments: attachedFiles.length > 0,
        messageLength: input.length
      }
    });

    // Initialize model cache immediately if user sends message before 3 second timeout
    if ((window as any).initializeModelCacheNow) {
      await (window as any).initializeModelCacheNow();
    }
    const userMessage = {
      id: generateId(),
      role: 'user' as const,
      content: input,
      attachments: attachedFiles.length > 0 ? attachedFiles : undefined,
      timestamp: Date.now(),
    };

    const assistantMessage = {
      id: generateId(),
      role: 'assistant' as const,
      content: '',
      timestamp: Date.now(),
      done: false,
      model: selectedModel,
      modelName: models.find((m) => m.id === selectedModel)?.name,
    };

    setMessages((prev) => [...prev, userMessage, assistantMessage]);
    setInput('');
    setAttachedFiles([]);
    setIsLoading(true);

    // Create abort controller for cancellation
    const controller = new AbortController();
    setAbortController(controller);

    let response = '';

    try {
      if (engine && status === 'ready') {
        // Use WebLLM with streaming if available
        console.log('Using WebLLM for streaming response');
        setIsStreaming(true);

        try {
          const chunks = await engine.chat.completions.create({
            messages: [
              { role: 'system', content: chatParameters.systemMessage },
              ...messages.map((m) => ({ role: m.role, content: m.content })),
              { role: 'user', content: input }
            ],
            stream: true,
            stream_options: { include_usage: true },
            temperature: chatParameters.temperature,
            max_tokens: chatParameters.maxTokens,
            top_p: chatParameters.topP,
            presence_penalty: chatParameters.presencePenalty,
            frequency_penalty: chatParameters.frequencyPenalty,
          });

          // Handle streaming response with cancellation support
          let fullResponse = '';
          for await (const chunk of chunks) {
            // Check if cancelled
            if (controller.signal.aborted) {
              console.log('Streaming cancelled by user');
              break;
            }

            const delta = chunk.choices[0]?.delta.content || "";
            fullResponse += delta;

            // Update the assistant message content in real-time
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantMessage.id
                  ? { ...msg, content: fullResponse }
                  : msg
              )
            );
          }

          response = fullResponse || 'No response generated';
        } finally {
          setIsStreaming(false);
          setAbortController(null);
        }

        // Mark the message as done
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantMessage.id
              ? { ...msg, done: true }
              : msg
          )
        );
      } else {
        // Fallback to mock response while model loads
        console.log('WebLLM not ready, using mock streaming simulation');
        setIsStreaming(true);
        await new Promise(resolve => setTimeout(resolve, 500));

        const supportMessage = !isSupported
          ? ' WebLLM is not supported in this environment (requires WebAssembly and Cache API).'
          : '';

        const mockStatusMessage = status === 'error'
          ? 'Model loading failed. Try selecting a different model or check browser compatibility.'
          : status === 'loading'
          ? `Model is loading (${progress}%)... Please wait.`
          : `Model not ready (status: ${status}). Please select a model from the dropdown.`;

        try {
          // Simulate streaming for mock response
          const mockResponse = `${chatParameters.systemMessage ? `[System: ${chatParameters.systemMessage}]\n` : ''}I understand you said: "${input}". ${mockStatusMessage}${supportMessage}`;
          const words = mockResponse.split(' ');
          let currentResponse = '';

          for (const word of words) {
            currentResponse += (currentResponse ? ' ' : '') + word;
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantMessage.id
                  ? { ...msg, content: currentResponse }
                  : msg
              )
            );
            await new Promise(resolve => setTimeout(resolve, 100));
          }

          response = mockResponse;
        } finally {
          setIsStreaming(false);
        }

        // Mark the message as done
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantMessage.id
              ? { ...msg, done: true }
              : msg
          )
        );
      }
    } catch (error) {
      response = `Error: ${error instanceof Error ? error.message : 'Unknown error'}`;
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantMessage.id ? { ...msg, content: response, done: true } : msg
        )
      );

      // Log failed interaction
      await logInteraction({
        toolUsed: 'chat',
        query: input,
        response: response,
        success: false,
        metadata: {
          model: selectedModel,
          error: error instanceof Error ? error.message : 'Unknown error',
          hasAttachments: attachedFiles.length > 0
        }
      });
    }

    setIsLoading(false);

    const fullMessages = [...messages, userMessage, { ...assistantMessage, content: response, done: true }];

    // ✅ Save chat to LOCAL IndexedDB FIRST (offline first)
    try {
      if (!currentChatId && messages.length === 0) {
        const title = input.slice(0, 50) + (input.length > 50 ? '...' : '');
        const chatId = generateId();

        // Save to local database
        await mawiDB.store.createChatHistory({
          userId: user?.id || 'anonymous',
          title,
          messages: fullMessages,
          modelId: selectedModel,
          totalTokens: 0,
          tags: [],
          isArchived: false
        });

        setCurrentChatId(chatId);
        setChatTitle(title);
        window.history.replaceState({}, '', `/c/${chatId}`);
      } else if (currentChatId) {
        // Update existing chat in local database
        await mawiDB.store.updateChatHistory(currentChatId, {
          messages: fullMessages,
          updatedAt: new Date().toISOString()
        });
      }

      console.log('Chat saved successfully to local IndexedDB');
    } catch (localSaveErr) {
      console.warn('Failed to save chat locally, falling back to API:', localSaveErr);
    }

    // Save chat via API (fallback / sync)
    if (!currentChatId && messages.length === 0) {
      const title = input.slice(0, 50) + (input.length > 50 ? '...' : '');
      const newChat = await createChat({
        id: generateId(),
        title,
        models: [selectedModel],
        messages: fullMessages,
        history: { messages: {}, currentId: assistantMessage.id },
        timestamp: Date.now(),
      });
      setCurrentChatId(newChat.id);
      setChatTitle(title);
      window.history.replaceState({}, '', `/c/${newChat.id}`);
    } else if (currentChatId) {
      // Update existing chat
      await updateChat(currentChatId, {
        messages: fullMessages,
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Check microphone permissions
  const checkMicrophonePermissions = async (): Promise<boolean> => {
    console.log('Checking microphone permissions...');

    try {
      // Check if Permissions API is supported
      if ('permissions' in navigator) {
        try {
          console.log('Using Permissions API to check status...');
          // Add timeout to prevent hanging
          const permissionPromise = navigator.permissions.query({ name: 'microphone' as PermissionName });
          const timeoutPromise = new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Permissions API timeout')), 5000)
          );

          const permissionStatus = await Promise.race([permissionPromise, timeoutPromise]);
          console.log('Microphone permission status:', permissionStatus.state);

          if (permissionStatus.state === 'granted') {
            setMicPermissionStatus('granted');
            return true;
          } else if (permissionStatus.state === 'denied') {
            setMicPermissionStatus('blocked');
            return false;
          } else {
            // permission is 'prompt' - browser default state
            setMicPermissionStatus('prompt');
            return false;
          }
        } catch (permissionError: any) {
          console.log('Permissions API query failed, falling back to getUserMedia test:', permissionError.message);
        }
      }

      // Fallback: try to get user media to check current permission state
      // This will succeed if already granted, fail if denied or prompt needed
      try {
        console.log('Testing microphone access with getUserMedia...');
        // Add timeout to prevent hanging
        const mediaPromise = navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false }
        });
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('getUserMedia timeout')), 5000)
        );

        const stream = await Promise.race([mediaPromise, timeoutPromise]);
        stream.getTracks().forEach(track => track.stop()); // Stop immediately
        console.log('Microphone access test successful - permissions granted');
        setMicPermissionStatus('granted');
        return true;
      } catch (mediaError: any) {
        console.log('Microphone access test failed:', mediaError.name, mediaError.message);

        if (mediaError.name === 'NotAllowedError') {
          setMicPermissionStatus('blocked');
        } else {
          // Other errors (NotFoundError, timeout, etc.) - treat as prompt needed
          setMicPermissionStatus('prompt');
        }
        return false;
      }
    } catch (error) {
      console.error('Error checking microphone permissions:', error);
      // If all else fails, assume prompt state
      setMicPermissionStatus('prompt');
      return false;
    }
  };

  // Open browser microphone settings page
  const openMicrophoneSettings = () => {
    console.log('Opening browser microphone settings...');
    const userAgent = navigator.userAgent.toLowerCase();

    try {
      if (userAgent.includes('edg') || userAgent.includes('edge')) {
        // Microsoft Edge
        console.log('Detected Edge browser');
        window.open('edge://settings/content/microphone', '_blank');
      } else if (userAgent.includes('chrome') || userAgent.includes('chromium')) {
        // Chrome/Opera
        console.log('Detected Chrome/Chromium browser');
        window.open('chrome://settings/content/microphone', '_blank');
      } else if (userAgent.includes('firefox')) {
        // Firefox
        console.log('Detected Firefox browser');
        window.open('about:preferences#privacy', '_blank');
      } else if (userAgent.includes('safari')) {
        // Safari - no direct settings URL
        console.log('Detected Safari browser');
        alert('Please go to Safari Preferences → Websites → Microphone to enable permissions for this site.\n\n1. Open Safari Preferences (Cmd+,)\n2. Go to "Websites" tab\n3. Select "Microphone" from the left sidebar\n4. Find this website and set microphone to "Allow"');
      } else {
        // Generic fallback
        console.log('Unknown browser, showing generic instructions');
        alert(`Please check your browser settings to enable microphone permissions for this site.\n\nCommon locations:\n• Chrome/Edge: chrome://settings/content/microphone\n• Firefox: about:preferences#privacy\n• Safari: Preferences → Websites → Microphone`);
      }
    } catch (error) {
      console.error('Failed to open browser settings:', error);
      alert('Unable to open browser settings automatically. Please manually navigate to your browser\'s microphone permission settings.');
    }
  };

  // Request microphone permissions - triggers browser's native popup
  const requestMicrophonePermissions = async (): Promise<boolean> => {
    console.log('Requesting microphone permissions...');

    try {
      // Use getUserMedia to trigger the browser's native permission popup
      console.log('Requesting microphone permissions via getUserMedia...');
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      console.log('Microphone permissions granted successfully');
      stream.getTracks().forEach(track => track.stop()); // Stop immediately after getting permission
      setMicPermissionStatus('granted');
      return true;

    } catch (error: any) {
      console.error('Microphone permission request failed:', {
        name: error.name,
        message: error.message
      });

      setMicPermissionStatus('blocked');

      // Don't show alerts - let the UI handle user guidance
      // The permission banner will show appropriate messaging based on the error
      return false;
    }
  };

  // Check microphone permissions on component mount
  useEffect(() => {
    const initializePermissions = async () => {
      console.log('Initializing microphone permissions...');
      const hasPermission = await checkMicrophonePermissions();
      console.log('Microphone permission check completed:', hasPermission ? 'granted' : 'not granted');
    };

    initializePermissions();
  }, []);

  // Initialize speech recognition service
  useEffect(() => {
    // Set up callbacks for the speech recognition service
    speechRecognitionService.setCallbacks({
      onStart: () => {
        setIsRecording(true);
        console.log('Speech recognition started via service');
      },
      onResult: (transcript: string, isFinal: boolean) => {
        if (isFinal) {
          setTranscript(transcript);
          // Append final transcript to input
          setInput((prev) => prev + ' ' + transcript);
        }
      },
      onEnd: () => {
        setIsRecording(false);
        console.log('Speech recognition ended via service');
      },
      onError: (error: string) => {
        console.error('Speech recognition error via service:', error);
        setIsRecording(false);
      }
    });

    // Cleanup on unmount
    return () => {
      speechRecognitionService.setCallbacks({});
    };
  }, []);

  // Keyboard shortcuts
  useKeyboardShortcuts({
    shortcuts: [
      {
        key: 'Enter',
        ctrlKey: true,
        action: () => {
          if (input.trim() && !isLoading) {
            sendMessage();
          }
        },
        description: 'Send message',
      },
      {
        key: 'm',
        ctrlKey: true,
        action: () => setShowModelSelector(true),
        description: 'Open model selector',
      },
      {
        key: 'p',
        ctrlKey: true,
        action: () => setShowParameters(!showParameters),
        description: 'Toggle parameters panel',
      },
    ],
  });

  // Touch gestures for mobile
  useTouchGestures({
    onSwipeLeft: () => {
      // Could navigate to next chat
      console.log('Swipe left - next chat');
    },
    onSwipeRight: () => {
      // Could navigate to previous chat
      console.log('Swipe right - previous chat');
    },
  });

  // Copy input text to clipboard
  const copyInputToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(input);
      // Could add a toast notification here in the future
      console.log('Input text copied to clipboard');
    } catch (error) {
      console.error('Failed to copy text:', error);
      // Fallback for older browsers
      try {
        const textArea = document.createElement('textarea');
        textArea.value = input;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        console.log('Input text copied to clipboard (fallback)');
      } catch (fallbackError) {
        console.error('Fallback copy also failed:', fallbackError);
      }
    }
  };

  const handleImageUpload = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.onchange = (e) => {
      const files = Array.from((e.target as HTMLInputElement).files || []);
      setAttachedFiles(prev => [...prev, ...files]);
    };
    input.click();
  }, []);

  const handleCodeSnippet = useCallback(() => {
    const codeTemplate = `\`\`\`javascript
// Your code here
function example() {
    console.log("Hello, World!");
}
\`\`\``;

    setInput(prev => prev + (prev ? '\n\n' : '') + codeTemplate);
  }, []);

  const toggleRecording = async () => {
    // Check if speech recognition is supported
    if (!speechRecognitionService.isSupported()) {
      console.log('Speech recognition not supported');
      alert('Speech recognition is not supported in your browser. Please use a modern browser like Chrome, Firefox, Safari, or Edge.');
      return;
    }

    // Check permissions first
    if (micPermissionStatus === 'blocked') {
      // Try to request permissions
      const granted = await requestMicrophonePermissions();
      if (!granted) return;
    }

    if (micPermissionStatus === 'prompt') {
      // Request permissions
      const granted = await requestMicrophonePermissions();
      if (!granted) return;
    }

    if (isRecording) {
      speechRecognitionService.stop();
    } else {
      setTranscript('');
      const started = await speechRecognitionService.start();
      if (!started) {
        console.error('Failed to start speech recognition service');
      }
    }
  };

  // Model persistence status
  const getModelStatus = () => {
    if (status === 'ready') {
      return { text: 'Ready', color: 'text-green-600', bg: 'bg-green-100 dark:bg-green-900' };
    } else if (status === 'loading') {
      return { text: `Loading ${Math.round(progress)}%`, color: 'text-blue-600', bg: 'bg-blue-100 dark:bg-blue-900' };
    } else if (status === 'error') {
      return { text: 'Error', color: 'text-red-600', bg: 'bg-red-100 dark:bg-red-900' };
    } else if (backend === 'service-worker') {
      return { text: 'Cached', color: 'text-green-600', bg: 'bg-green-100 dark:bg-green-900' };
    } else if (backend === 'web-worker') {
      return { text: 'Optimized', color: 'text-blue-600', bg: 'bg-blue-100 dark:bg-blue-900' };
    }
    return null;
  };

  const modelStatus = getModelStatus();

  const promptSuggestions = config?.default_prompt_suggestions || [
    { title: ['Explain', 'a complex topic'], content: 'Explain quantum computing in simple terms' },
    { title: ['Write', 'code'], content: 'Write a Python function to sort a list' },
    { title: ['Summarize', 'text'], content: 'Summarize the key points of this article' },
    { title: ['Brainstorm', 'ideas'], content: 'Brainstorm ideas for a birthday party' },
  ];

  // Connect to global header system
  const { setLeftContent, clearHeader } = usePageHeader();

  useEffect(() => {
    // Render chat controls into global nav bar
    setLeftContent(
      <div className="flex items-center gap-2">
        {currentChatId && (
          <Button
            variant="default"
            size="sm"
            className="h-8 w-8 p-0 rounded-full bg-blue-500 hover:bg-blue-600 text-white"
            title="Start New Chat"
            onClick={() => {
              setCurrentChatId(null);
              setChatTitle('New Chat');
              setMessages([]);
              setShowChatHistory(false);
              window.history.replaceState({}, '', '/dashboard');
            }}
          >
            <span className="font-bold text-lg">+</span>
          </Button>
        )}
        <Breadcrumb
          items={[
            { label: 'Chat', href: '/dashboard' },
            { label: chatTitle || 'New Chat' }
          ]}
        />
        
        <div className="relative ml-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            title="Chat History"
            onClick={() => setShowChatHistory(!showChatHistory)}
          >
            <History className="h-4 w-4" />
          </Button>

          {/* Chat History Panel */}
          {showChatHistory && (
            <div className="absolute -left-[180px] top-full mt-1 w-80 max-h-96 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-50 overflow-hidden">
              <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <h4 className="font-semibold text-sm">Chat History</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => setShowChatHistory(false)}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
              <ScrollArea className="max-h-72">
                {chatHistoryList.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    No previous chats found
                  </div>
                ) : (
                  <div className="p-1">
                    {chatHistoryList.map((chat) => (
                      <button
                        key={chat.id}
                        className="w-full text-left p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-sm"
                        onClick={() => {
                          setCurrentChatId(chat.id);
                          setChatTitle(chat.title);
                          setMessages(chat.messages || []);
                          setShowChatHistory(false);
                          window.history.replaceState({}, '', `/c/${chat.id}`);
                        }}
                      >
                        <div className="font-medium truncate">{chat.title}</div>
                        <div className="text-xs text-gray-500">
                          {formatDate(chat.updatedAt || chat.createdAt)}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          )}
        </div>
      </div>
    );

    // Clear header on unmount
    return () => clearHeader();
  }, [currentChatId, chatTitle, showChatHistory]);

  // Helper function to get short model display name
  const getModelShortName = (fullName: string) => {
    if (!fullName) return 'Unknown';
    
    // Extract clean short name from full model identifier
    let short = fullName
      .replace(/instruct$/i, '')
      .replace(/v\d+.*$/i, '')
      .replace(/[\-_\.].*$/, '')
      .trim();
    
    return short.charAt(0).toUpperCase() + short.slice(1).toLowerCase();
  };

  return (
    <div className="flex flex-col h-full">

      {/* Messages */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 py-8">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full min-h-[60vh] text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                <Sparkles className="h-8 w-8 text-primary" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Welcome to Mawi AI Assist</h1>
              <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md">
                Start a conversation with AI Agent. Ask questions, get help with code, or just chat!
              </p>

              {/* Prompt suggestions */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full max-w-2xl">
                {promptSuggestions.map((suggestion, idx) => (
                  <button
                    key={idx}
                    onClick={() => setInput(suggestion.content)}
                    className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 text-left transition-colors"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      {suggestion.title.map((word, i) => (
                        <span
                          key={i}
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            i === 0
                              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                              : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                          }`}
                        >
                          {word}
                        </span>
                      ))}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {suggestion.content}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          ) : messagesLoading ? (
            <div className="space-y-6">
              {Array.from({ length: 3 }).map((_, i) => (
                <SkeletonChatMessage key={`skeleton-${i}`} />
              ))}
            </div>
          ) : messages.length > 20 ? (
            <Virtuoso
              data={messages}
              overscan={10}
              increaseViewportBy={{ top: 400, bottom: 400 }}
              itemContent={(index, message) => (
                <div
                  key={message.id}
                  className={cn(
                    'flex gap-2 sm:gap-4 py-4 px-2 sm:px-0',
                    message.role === 'user' ? 'flex-row-reverse' : ''
                  )}
                >
                  <div
                    className={cn(
                      'w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0',
                      message.role === 'user'
                        ? 'bg-blue-500 text-white'
                        : 'bg-green-500 text-white'
                    )}
                  >
                    {message.role === 'user' ? (
                      <span className="text-xs sm:text-sm font-medium">
                        {user?.name?.charAt(0) || 'U'}
                      </span>
                    ) : (
                      <Sparkles className="h-3 w-3 sm:h-4 sm:w-4" />
                    )}
                  </div>
                  <div
                    className={cn(
                      'flex-1 max-w-[85%] sm:max-w-[80%]',
                      message.role === 'user' ? 'text-right' : ''
                    )}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium">
                        {message.role === 'user'
                          ? user?.name || 'You'
                          : message.modelName || 'Assistant'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatDate(message.timestamp)}
                      </span>
                      {message.role === 'assistant' && backend !== 'main-thread' && (
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          backend === 'service-worker'
                            ? 'bg-green-100 text-green-600'
                            : 'bg-blue-100 text-blue-600'
                        }`}>
                          {backend === 'service-worker' ? 'Persistent' : 'Web Worker'}
                        </span>
                      )}
                    </div>
                    <EnhancedMessage
                      message={message}
                      onCopy={(content) => {
                        navigator.clipboard.writeText(content);
                        console.log('Message copied to clipboard');
                      }}
                      onSave={(msg) => {
                        // Save message functionality
                        console.log('Saving message:', msg);
                      }}
                      onShare={(msg) => {
                        // Share message functionality
                        console.log('Sharing message:', msg);
                      }}
                    />

                    {/* Show thinking indicator for incomplete AI messages */}
                    {message.role === 'assistant' && !message.done && (
                      <div className="mt-2">
                        {!message.content.trim() && (
                          <ThinkingIndicator status={status} progress={progress} isStreaming={isStreaming} />
                        )}
                        {message.content.trim() && (
                          <div className="flex items-center gap-1 mt-2">
                            <div className="flex gap-1">
                              <div className="w-1 h-1 bg-gray-400 rounded-full animate-pulse"></div>
                              <div className="w-1 h-1 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '200ms' }}></div>
                              <div className="w-1 h-1 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '400ms' }}></div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
              style={{ height: '100%' }}
            />
          ) : (
            <div className="space-y-6">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={cn(
                    'flex gap-2 sm:gap-4',
                    message.role === 'user' ? 'flex-row-reverse' : ''
                  )}
                >
                  <div
                    className={cn(
                      'w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0',
                      message.role === 'user'
                        ? 'bg-blue-500 text-white'
                        : 'bg-green-500 text-white'
                    )}
                  >
                    {message.role === 'user' ? (
                      <span className="text-xs sm:text-sm font-medium">
                        {user?.name?.charAt(0) || 'U'}
                      </span>
                    ) : (
                      <Sparkles className="h-3 w-3 sm:h-4 sm:w-4" />
                    )}
                  </div>
                  <div
                    className={cn(
                      'flex-1 max-w-[85%] sm:max-w-[80%]',
                      message.role === 'user' ? 'text-right' : ''
                    )}
                  >
                    <div
                      className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4"
                      role="article"
                      aria-label={`${message.role === 'user' ? 'Your message' : 'AI response'}: ${message.content.substring(0, 100)}${message.content.length > 100 ? '...' : ''}`}
                    >
                      <div className="text-sm whitespace-pre-wrap">
                        {message.content}
                      </div>

                      {/* Display attachments */}
                      {message.attachments && message.attachments.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {message.attachments.map((file: File, index: number) => (
                            <div key={index} className="relative">
                              {file.type.startsWith('image/') ? (
                                <img
                                  src={URL.createObjectURL(file)}
                                  alt={file.name}
                                  className="max-w-32 max-h-32 object-cover rounded border"
                                />
                              ) : (
                                <div className="flex items-center gap-2 p-2 bg-background rounded border text-xs">
                                  <Paperclip className="h-3 w-3" />
                                  <span className="truncate max-w-24">{file.name}</span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {!message.done && message.content.trim() === '' && (
                        <ThinkingIndicator status={status} progress={progress} isStreaming={isStreaming} />
                      )}
                      {message.role === 'assistant' && !message.done && message.content.trim() !== '' && (
                        <div className="flex items-center gap-1 mt-2">
                          <div className="flex gap-1">
                            <div className="w-1 h-1 bg-gray-400 rounded-full animate-pulse"></div>
                            <div className="w-1 h-1 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '200ms' }}></div>
                            <div className="w-1 h-1 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '400ms' }}></div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="ml-2 h-6 px-2"
                            onClick={() => {
                              console.log('Stopping streaming generation...');
                              if (abortController) {
                                abortController.abort();
                                setAbortController(null);
                              }
                              setIsLoading(false);
                              setIsStreaming(false);
                            }}
                          >
                            <StopCircle className="h-3 w-3 mr-1" />
                            Stop
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
         <div ref={messagesEndRef} />
       </div>

      {/* Chat Input Container */}
      <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 md:p-6">
        <div className="max-w-3xl mx-auto">
          <AIChatInput
        modelStatus={modelStatus}
        input={input}
        onInputChange={setInput}
        onSend={sendMessage}
        selectedModel={selectedModel}
        onModelChange={setSelectedModel}
        availableModels={models}
        onStop={() => {
          if (abortController) {
            abortController.abort();
            setAbortController(null);
          }
          setIsLoading(false);
          setIsStreaming(false);
        }}
        onVoiceToggle={toggleRecording}
        onFileAttach={() => fileInputRef.current?.click()}
        onImageUpload={handleImageUpload}
        isLoading={isLoading}
        isRecording={isRecording}
        isStreaming={isStreaming}
        micPermissionStatus={micPermissionStatus}
        attachedFiles={attachedFiles}
        onRemoveFile={(index) => setAttachedFiles(prev => prev.filter((_, i) => i !== index))}
          />
        </div>
      </div>

        {/* Model Selector Modal */}
        {showModelSelector && (
          <div className="fixed inset-0 z-[9998] bg-black/50 dark:bg-black/70" onClick={() => setShowModelSelector(false)}>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-[9999] max-h-96 overflow-y-auto">
              <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">Select Model</h3>
              </div>

              <div className="p-2">
                {models.length === 0 ? (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    Loading models...
                  </div>
                ) : (
                  models.map((model, index) => (
                    <button
                      key={model.id}
                      onClick={async () => {
                        if (modelSwitchInProgress) {
                          console.log('Model switch already in progress, ignoring');
                          return;
                        }

                        console.log('Model selected:', model.id);
                        setSelectedModel(model.id);
                        setShowModelSelector(false);
                        setModelSwitchInProgress(true);

                        try {
                          console.log('Calling switchModel for:', model.id);
                          await switchModel(model.id);
                          console.log('switchModel completed for:', model.id);
                        } catch (error) {
                          console.error('Failed to switch model:', error);
                        } finally {
                          setModelSwitchInProgress(false);
                        }
                      }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800 rounded mb-1 transition-colors ${
                        selectedModel === model.id
                          ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700'
                          : 'text-gray-900 dark:text-gray-100'
                      }`}
                    >
                       <div className="flex items-center gap-1">
                         <span className="font-medium truncate">{getModelShortName(model.name)}</span>
                        {cachedModels.includes(model.id) && (
                          <span className="text-[10px] bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400 px-1 py-0.5 rounded">
                            Cached
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">{model.owned_by}</div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
    </div>
  );
}

export default Chat;
